import { Text, Group, Tooltip, Anchor } from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import { EntityParams } from "app/models/entity-params"
import { DealParams } from "app/models/deal-params"
import { FacilityParams } from 'app/models/facility-params'
import { EntityProfileParams } from "app/models/entity-profile-params"
import { EntityTypeParams } from "app/models/entity-type-params"
import { EntityTypeHierarchy } from "app/models/entity-type-params"
import { ContactParams } from "app/models/contact-params"
import { HolidayCalendarParams } from "app/models/holiday-calendar-params"
import { IndexRateOptionParams } from 'app/models/index-rate-options-params'
import { Fees } from 'app/models/fees-params'
import { institutionFinancialTypeOptions } from "app/models/dropdown-options"
import { kycStatusOptions } from "app/models/dropdown-options"
import { toTitleCase, formatDateToShow, formatNumberToCurrency, stringToDate } from "app/utils/util-functions"
import cleanUUID from 'app/views/components/functions/cleanUUID'
import getInitials from "app/views/components/functions/getInitials"
import PlusCountIcon from 'app/views/components/icons/plus-count-icon'
import CustomHoverCard from 'app/views/components/popover/custom-hover-card'

import { StableKey } from 'app/models/common-types'


export const buildEntityDetailsObject = (entity: EntityParams, entityProfiles: EntityProfileParams[], entityTypes: EntityTypeParams[], isTaxFormExpired: boolean) => {
    const entityType = entityTypes?.find(type => type.id === entity?.entityType?.id)
    const isFundManager = entityType?.hierarchy
        ? [EntityTypeHierarchy.HierarchyFundManager, EntityTypeHierarchy.HierarchyBoth].includes(entityType?.hierarchy)
        : false

    return {
        entityDetails: [
            { label: 'Entity Type', value: entityType?.name || 'N/A' },
            { label: isFundManager ? 'Fund Manager' : 'Parent', value: isFundManager ? entity?.fundManager : entity?.parent },
            { label: isFundManager ? 'Fund Manager MEI' : 'Parent MEI', value: isFundManager ? entity?.fundManagerMei : entity?.parentMei },
            { label: 'Institution Type', value: institutionFinancialTypeOptions.find(option => option.value === entity?.institutionType)?.label || toTitleCase(entity?.institutionType ?? 'N/A') },
            { label: 'Entity Profile', value: entity?.entityProfile.map(profile => entityProfiles.find(option => option.id === profile.id)?.name || '').filter(Boolean).join(', ') ?? 'N/A' },
            { label: 'KYC Status', value: kycStatusOptions.find(option => option.value === entity?.kycStatus)?.label || 'Pending' },
        ],
        addressDetails: [
            { label: 'Address', value: entity?.address },
            { label: 'Address Line 2', value: entity?.address2 },
            { label: 'City', value: entity?.city },
            { label: 'State/Region', value: entity?.state },
            { label: 'ZIP/Postal Code', value: entity?.zipCode },
            { label: 'Country of Incorporation', value: entity?.countryOfIncorporation },
        ],
        identifiersDetails: [
            { label: 'Entity ID', value: entity?.systemId },
            { label: 'MEI', value: entity?.mei },
            { label: 'LEI', value: entity?.legalEntityId },
            { label: 'Internal Entity ID', value: entity?.internalId },
            { label: 'CRN #', value: entity?.crn },
            { label: 'GIIN', value: entity?.giin },
            { label: 'CEI', value: entity?.cei },
            { label: 'Agent Entity ID', value: entity?.agentEntityId },
            { label: 'NAIC Code', value: entity?.naicCode },
        ],
        taxDetails: [
            { label: 'Entity Reference', value: entity?.entityReference },
            { label: 'US Tax Form Status', value: isTaxFormExpired ? <div className="entity-tax-form-status"><IonIcon className="expired-alert-icon" name="alert-circle-sharp" />Expired</div> : entity?.taxFormStatus ? entity.taxFormStatus.replaceAll('_', ' ') : undefined },
            { label: 'Tax Form Expiration Date', value: entity?.taxFormExpirationDate ? formatDateToShow(stringToDate(entity?.taxFormExpirationDate)) : undefined },
            { label: 'US Tax Form Type', value: entity?.usTaxFormType },
            ...(entity?.countryOfTaxResidence === "GB" ? [{ label: 'UK Treaty Passport #', value: entity?.ukTreatyPassportNumber }] : []),
            { label: 'EIN', value: entity?.ein },
            { label: 'Country Of Tax Residence', value: entity?.countryOfTaxResidence },
            ...(entity?.countryOfTaxResidence === "GB" ? [{ label: 'UK Treaty Passport Exp Date', value: entity?.ukTreatyPassportExpiryDate }] : []),
        ],
        signatureBlock: [
            {
                label: 'Signature Block', value: entity?.signatureBlock?.signatureText
                    ? <div dangerouslySetInnerHTML={{ __html: entity.signatureBlock.signatureText }} />
                    : 'No Signature Block details available.'
            }
        ],
        signatory: [
            { label: 'Name', value: entity?.signatureBlock?.signatureName ?? 'No Signatory Details for Name available.' },
            { label: 'Title', value: entity?.signatureBlock?.signatureTitle ?? 'No Signatory Details for Title available.' },
            { label: 'Email', value: entity?.signatureBlock?.signatureEmail ?? 'No Signatory Details for Email available.' }
        ]
    }
}

export function buildEntityDetailSectionForEntityTabs(entity: EntityParams, entityProfiles: EntityProfileParams[], entityTypes: EntityTypeParams[]) {
    const entityType = entityTypes?.find(type => type.id === entity?.entityType?.id)
    const isFundManager = entityType?.hierarchy
        ? [EntityTypeHierarchy.HierarchyFundManager, EntityTypeHierarchy.HierarchyBoth].includes(entityType?.hierarchy)
        : false
    return {
        entityDetails: [
            { label: 'Entity Type', value: entityType?.name || 'N/A' },
            { label: 'Entity Profile', value: entity?.entityProfile.map(profile => entityProfiles.find(option => option.id === profile.id)?.name || '').filter(Boolean).join(', ') ?? 'N/A' },
            { label: 'MEI', value: entity?.mei },
            { label: 'GIIN', value: entity?.giin },
            { label: isFundManager ? 'Fund Manager' : 'Parent', value: isFundManager ? entity?.fundManager : entity?.parent },
            { label: isFundManager ? 'Fund Manager MEI' : 'Parent MEI', value: isFundManager ? entity?.fundManagerMei : entity?.parentMei },
        ],
        addressDetails: [
            { label: 'Address', value: entity?.address },
            { label: 'Address Line 2', value: entity?.address2 },
            { label: 'City', value: entity?.city },
            { label: 'State/Region', value: entity?.state },
            { label: 'ZIP/Postal Code', value: entity?.zipCode },
            { label: 'Country of Incorporation', value: entity?.countryOfIncorporation },
        ],
    }
}


export function buildDealDetailSections(
    deal: DealParams,
    entities: EntityParams[],
    contacts: ContactParams[],
    holidayCalendars: HolidayCalendarParams[],
    availableCurrencies: string[],
    navigate: (to: string, options?: { replace?: boolean; state?: any }) => void
) {
    function dealCalendars() {
        if (holidayCalendars && deal?.holidayCalendar?.length > 0) {
            const dealCals = deal.holidayCalendar.map((calendar: any) =>
                holidayCalendars.find(hc => hc.id === calendar.id)?.name
            )
            return dealCals.join(', ')
        }
        return ''
    }

    const dealDetailsObjectArray = [
        { label: 'Start Date', value: formatDateToShow(deal ? stringToDate(deal.startDate) : undefined) },
        { label: 'Maturity Date', value: formatDateToShow(deal ? stringToDate(deal.maturity) : undefined) },
        { label: "CP's Met", value: formatDateToShow(deal?.precedentDate ? stringToDate(deal.precedentDate) : undefined) },
        { label: 'Original Amount', value: deal?.originalAmount ? formatNumberToCurrency(Number(deal?.originalAmount), deal?.currency ?? 'USD') : undefined },
        { label: 'Current Amount', value: formatNumberToCurrency(Number(deal?.amount) ?? 0, deal?.currency ?? 'USD') },
        {
            label: 'Utilized Amount',
            value: <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {formatNumberToCurrency(Number(deal?.utilizedAmount) ?? 0, deal?.currency ?? 'USD')}
                {Number(deal?.utilizedAmount) > Number(deal?.amount) &&
                    <Tooltip label="Utilized amount exceeds the current deal amount" withArrow>
                        <span style={{ display: 'inline-flex', transform: 'translateY(-2px)' }}>
                            <IonIcon className="expired-alert-icon" name="alert-circle-sharp" />
                        </span>
                    </Tooltip>
                }
            </span>
        },
        {
            label: 'Administrative Agent',
            value: (() => {
                const adminEntities = deal?.adminEntity?.map(adminEntity =>
                    entities?.find(entity => entity?.id === cleanUUID(adminEntity?.id))?.entityName ?? ''
                ).filter(Boolean);

                if (adminEntities.length === 0) return undefined;
                if (adminEntities.length === 1) return adminEntities[0];
                return adminEntities.join(', ');
            })()
        },
        {
            label: 'Administrative Agent MEI',
            value: deal?.adminEntity.length === 0 ? undefined : deal?.adminEntity?.map(singleAdminEntity =>
                entities?.find(entity => entity?.id === cleanUUID(singleAdminEntity?.id))?.mei ?? ''
            ).join(', ')

        },
        { label: 'Base Currency', value: deal?.currency },
        {
            label: 'White Label Agent',
            value: deal?.whiteLabelAgent?.id ? entities?.find(entity => entity.id === cleanUUID(deal?.whiteLabelAgent?.id))?.entityName : undefined
        },
        {
            label: 'White Label Agent MEI',
            value: deal?.whiteLabelAgent?.id ? entities?.find(entity => entity.id === cleanUUID(deal?.whiteLabelAgent?.id))?.mei : undefined
        },
        {
            label: <Text className='sectionItemTitleClickable' onClick={() =>
                navigate(
                    `/dealmanagement/deal/${deal?.id}/borrowers`,
                    { state: { deal, entities } }
                )
            }>Borrowers</Text>,
            value: deal?.borrowers.length === 0 ? undefined : deal?.borrowers?.map(borrower =>
                entities?.find(entity => entity?.id === cleanUUID(borrower?.id))?.entityName ?? ''
            ).join(', ')
        },
        {
            label: <Text className='sectionItemTitleClickable' onClick={() =>
                navigate(
                    `/dealmanagement/deal/${deal?.id}/guarantors`
                )}>Guarantors</Text>,
            value: deal?.guarantors.length === 0 ? undefined : deal?.guarantors?.map(guarantor =>
                entities?.find(entity => entity?.id === cleanUUID(guarantor?.id))?.entityName ?? ''
            ).join(', ')
        },
        {
            label: 'Available Currencies',
            value: availableCurrencies.length === 0 ? undefined : availableCurrencies.join(', ')
        },
        { label: 'Holiday Calendar', value: dealCalendars().length === 0 ? undefined : dealCalendars() },
        { label: 'Classification', value: deal?.classification },
        {
            label: 'Non-ProRata Trades',
            value: deal?.allowNonProRataAssignments != null
                ? deal.allowNonProRataAssignments
                    ? 'Yes'
                    : 'No'
                : 'N/A'
        }
    ]

    const dealIdentifiersObjectArray = [
        { label: 'Deal ID', value: deal?.systemId },
        { label: 'Deal CUSIP', value: deal?.cusip || undefined },
        { label: 'Deal ISIN', value: deal?.isin || undefined },
        { label: 'Client Deal ID', value: deal?.clientId || undefined },
        { label: 'Deal LXID', value: deal?.lxId || undefined },
        { label: 'Deal BBG ID', value: deal?.bbgId || undefined },
        { label: 'Agent Deal ID', value: deal?.agentDealId || undefined },
    ]

    const dealFinancialObjectArray = [
        {
            label: deal?.fees?.length > 0
                ? <Text className='sectionItemTitleClickable' onClick={() =>
                    navigate(`/dealmanagement/deal/${deal?.id}/${deal?.status}/fees/${deal?.fees?.[0]?.feeType}`)
                }>Fees</Text>
                : 'Fees',
            value: deal?.fees?.length?.toString() ?? '0'
        },
        {
            label: deal?.isPricingGrid
                ? <Text className='sectionItemTitleClickable' onClick={() =>
                    navigate(`/dealmanagement/deal/${deal?.id}/${deal?.status}/pricing-grid`)
                }>Pricing Grid</Text>
                : 'Pricing Grid',
            value: deal?.isPricingGrid ? toTitleCase(deal?.isPricingGrid.toString()) : 'False'
        },
    ]

    const dealContactsObjectArray = [
        {
            label: 'Deal Assignee',
            value: undefined,
        },
        {
            label: 'Administrative Agent Contact',
            value: deal?.opsAdmin && contacts
                ? deal.opsAdmin
                    .map(stableKey => contacts.find(contact => contact.id === cleanUUID(stableKey.id))?.name)
                    .filter(name => name !== undefined)
                    .join(', ') || ''
                : ''
        },
        {
            label: 'White Label Agent Contact',
            value: deal?.whiteLabelAgentContacts && contacts
                ? deal.whiteLabelAgentContacts
                    .map(stableKey => contacts.find(contact => contact.id === cleanUUID(stableKey.id))?.name)
                    .filter(name => name !== undefined)
                    .join(', ') || ''
                : ''
        },
    ]

    return {
        dealDetailsObjectArray,
        dealIdentifiersObjectArray,
        dealFinancialObjectArray,
        dealContactsObjectArray
    }
}

export function buildFacilityDetailSectionForFacilityTabs(
    facility: FacilityParams | undefined,
    entities: EntityParams[],
    deal: DealParams | undefined,
    navigate: (path: string, options?: any) => void
) {
    const facilityDetailsObjectArray = [
        {
            label: 'Deal ID',
            value: deal?.systemId,
        },
        {
            label: 'Total Amount',
            value: formatNumberToCurrency(Number(facility?.amount) ?? 0, facility?.baseCurrency ?? 'USD'),
        },
        {
            label: 'Currency',
            value: facility?.baseCurrency,
        },
        {
            label: (
                <Text
                    className="sectionItemTitleClickable"
                    onClick={() =>
                        navigate(`/dealmanagement/facilities/${facility?.id}/borrowers`, {
                            state: { facility, entities },
                        })
                    }
                >
                    Borrowers
                </Text>
            ),
            value: (
                <Group noWrap onClick={() =>
                    navigate(
                        `/dealmanagement/facilities/${facility?.id}/borrowers`
                    )
                }>
                    <Tooltip.Group openDelay={300} closeDelay={100}>
                        <div className="borrowers-avatar-group">
                            {(facility?.borrowers ?? []).slice(0, 5).map((borrower: StableKey, i) => {
                                const entityName =
                                    entities?.find(
                                        (entity) => entity?.id === cleanUUID(borrower?.id)
                                    )?.entityName ?? '';
                                return (
                                    <Anchor
                                        key={`borrower-initial-${borrower?.id}-${i}`}
                                        className={`borrowers-avatar ${getInitials(entityName).length === 1 ? 'single-initial-avatar' : ''}`}
                                    >
                                        <Tooltip label={entityName} withArrow>
                                            <span className="borrowers-avatar-text">
                                                {getInitials(entityName)}
                                            </span>
                                        </Tooltip>
                                    </Anchor>
                                );
                            })}
                        </div>
                        {facility?.borrowers && facility?.borrowers.length > 5 && (
                            <PlusCountIcon count={facility.borrowers.length - 5} />
                        )}
                    </Tooltip.Group>
                </Group>
            ),
        },
        {
            label: 'Start Date',
            value: formatDateToShow(facility?.startDate ? stringToDate(facility.startDate) : undefined),
        },
        {
            label: 'End Date',
            value: formatDateToShow(facility?.maturity ? stringToDate(facility.maturity) : undefined),
        },
        {
            label: 'Deal Assignee',
            value: undefined,
        },
    ]
    return facilityDetailsObjectArray
}


export function buildFacilityDetailSections(
    facility: FacilityParams,
    deal: DealParams,
    entityIdToName: Map<string, string>,
    entities: EntityParams[],
    contacts: ContactParams[],
    allIndexes: IndexRateOptionParams[],
    holidayCalendars: HolidayCalendarParams[],
    facilityFees: Fees[],
    isLoading: boolean,
    lendersPositions: any[],
    navigate: (path: string, options?: any) => void
) {
    // helper functions
    const dayBasisTransformAndJoin = (values: string[]): string => {
        return [...new Set(values.map(value => value.replace(/_/g, '/')))].join(', ');
    };

    const facilityCalendars = () => {
        if (holidayCalendars && facility?.holidayCalendar.length > 0) {
            const facilityCals = facility.holidayCalendar.map((calendar: any) =>
                holidayCalendars.find(hc => hc.id === calendar.id)?.name
            )
            return facilityCals.join(', ')
        }
        return undefined;
    }

    // Calculate lender allocations sum and difference
    const lenderAllocationsSum = lendersPositions?.reduce((sum, lender) => {
        const latestProRata = lender?.proRatas?.[lender.proRatas.length - 1]
        const amount = Number(latestProRata?.amount)
        return sum + (isNaN(amount) ? 0 : amount)
    }, 0) ?? 0

    const facilityAmount = Number(facility?.amount) || 0
    const allocationDifference = Math.abs(lenderAllocationsSum - facilityAmount)
    const isCommitted = facility?.status === 'Approved' || facility?.status === 'Locked'
    const hasAllocationMismatch = isCommitted && allocationDifference >= 0.01 && lendersPositions?.length > 0

    // Facility Detail Object Arrays
    const facilityDetailsObjectArray = [
        {
            label: 'Start Date',
            value: formatDateToShow(facility?.startDate ? stringToDate(facility.startDate) : undefined),
        },
        {
            label: 'Maturity Date',
            value: formatDateToShow(facility?.maturity ? stringToDate(facility.maturity) : undefined),
        },
        {
            label: 'Utilized Amount',
            value: formatNumberToCurrency(Number(facility?.utilizedAmount) ?? 0, facility?.baseCurrency ?? 'USD'),
        },
        {
            label: 'Original Amount',
            value: facility?.originalAmount ? formatNumberToCurrency(Number(facility?.originalAmount), facility?.baseCurrency ?? 'USD') : 'N/A',
        },
        {
            label: 'Current Amount',
            value: formatNumberToCurrency(Number(facility?.amount) ?? 0, facility?.baseCurrency ?? 'USD'),
        },
        {
            label: 'Base Currency',
            value: facility?.baseCurrency,
        },
        {
            label: 'Facility Type',
            value: facility?.facilityType,
        },
        {
            label: 'Available Currencies',
            value: facility?.currencies.length === 0 ? undefined : facility?.currencies?.join(', '),
        },
        {
            label: 'Classification',
            value: facility?.classification,
        },
        {
            label: 'Administrative Agent',
            value: entityIdToName.get(cleanUUID(facility?.adminEntity?.id)),
        },
        {
            label: 'Administrative Agent MEI',
            value: entities && deal
                ? entities.find(
                    entity =>
                        entity.id === cleanUUID(facility?.adminEntity?.id)
                )?.mei ?? ''
                : ''
        },
        {
            label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Text className='sectionItemTitleClickable' onClick={() =>
                        navigate(
                            `/dealmanagement/facilities/${facility?.id}/${facility?.status}/lenders`
                        )
                    }>Lender Allocations</Text>
                    {hasAllocationMismatch && (
                        <CustomHoverCard
                            cardTitle='Alert'
                            hoverTarget={
                                <IonIcon className="expired-alert-icon" name="alert-circle-sharp" />
                            }
                        >
                            <Text className='hover-card-alert-text'>
                                Sum of Facility Lender Shares do not equal Total Facility Amount. Difference: {formatNumberToCurrency(allocationDifference, facility?.baseCurrency ?? 'USD')}
                            </Text>
                        </CustomHoverCard>
                    )}
                </span>
            ),
            value: isLoading ? <></> : (
                <Group
                    noWrap
                    onClick={() =>
                        navigate(
                            `/dealmanagement/facilities/${facility?.id}/${facility?.status}/lenders`
                        )
                    }
                >
                    <Tooltip.Group openDelay={300} closeDelay={100}>
                        <div className='lenders-positions-avatar-group'>
                            {lendersPositions?.slice(0, 5).map((lender, i) => {
                                const lenderName =
                                    entities?.find(item => item.id === lender.lender.id)
                                        ?.entityName ?? ''
                                return (
                                    <Anchor
                                        key={'facility-accordion-lender-' + lender.id + i}
                                        className='lenders-positions-avatar'
                                    >
                                        <Tooltip label={lenderName} withArrow>
                                            <span className='lenders-positions-avatar-text'>
                                                {getInitials(lenderName)}
                                            </span>
                                        </Tooltip>
                                    </Anchor>
                                )
                            })}
                        </div>
                        {lendersPositions?.length > 5 && <PlusCountIcon count={lendersPositions.length - 5} />}
                    </Tooltip.Group>
                </Group>
            ),
        },
        {
            label: <Text className='sectionItemTitleClickable' onClick={() =>
                navigate(
                    `/dealmanagement/facilities/${facility?.id}/borrowers`,
                    { state: { facility, entities } }
                )
            }>Borrowers</Text>,
            value:
                <Group noWrap sx={{ gap: '10px' }}>
                    <Text>{facility?.borrowers?.map(borrower =>
                        entities?.find(entity => entity?.id === cleanUUID(borrower?.id))?.entityName ?? ''
                    )[0]}</Text>
                    {facility?.borrowers && facility?.borrowers.length > 1
                        ? <PlusCountIcon count={Math.max(0, (facility?.borrowers?.map(borrower =>
                            entities?.find(entity => entity?.id === cleanUUID(borrower?.id))?.entityName ?? ''
                        ) ?? []).length - 1)} />
                        : null}
                </Group>
        },
    ].concat(
        facility?.amendment
            ? [
                {
                    label: 'Amendment Date',
                    value: facility?.amendmentDate ?? undefined,
                },
            ]
            : []
    )

    const facilityIdentifiersObjectArray = [
        {
            label: 'Facility ID',
            value: facility?.systemId,
        },
        {
            label: 'Facility CUSIP',
            value: facility?.cusip,
        },
        {
            label: 'Facility ISIN',
            value: facility?.isin,
        },
        {
            label: 'Client Facility ID',
            value: facility?.clientId,
        },
        {
            label: 'Facility LXID',
            value: facility?.lxId,
        },
        {
            label: 'Facility BBG ID',
            value: facility?.bbgId
        },
        {
            label: 'Agent Facility ID',
            value: facility?.agentFacilityId,
        },
    ];

    const facilityFinancialsObjectArray = [
        {
            label: 'Interest Rate Option',
            value: facility?.iroValues.length === 0 ? undefined : facility?.iroValues.map(iro => allIndexes.find(index => index.id === iro.indexOption.id)?.indexOption).join(', ') ?? null,
        },
        {
            label: 'Day Basis',
            value: facility?.iroValues.length === 0 ? undefined : dayBasisTransformAndJoin(facility?.iroValues.map(iro => iro.dayBasis) ?? []),
        },
        {
            label: 'Holiday Calendar',
            value: facilityCalendars(),
        },
        {
            label: facilityFees?.length > 0
                ? <Text className='sectionItemTitleClickable' onClick={() =>
                    navigate(
                        `/dealmanagement/facilities/${facility?.id}/${facility?.status}/fees/${facilityFees[0]?.feeType}`
                    )
                }>Fees</Text>
                : 'Fees',
            value: facilityFees.length.toString() ?? '0',
        },
        {
            label: 'Sublimits',
            value: (facility?.subLimits?.length ?? 0) > 0 ? 'True' : 'False',
        },
        {
            label: 'Pricing Grid',
            value: (facility?.facilityPricingGrid?.length ?? 0) > 0 ? 'True' : 'False',
        },
        {
            label: facility?.pikScheduler
                ? <Text className='sectionItemTitleClickable' onClick={() =>
                    navigate(
                        `/dealmanagement/facilities/${facility?.id}/${facility?.status}/pik-schedule`
                    )
                }>PIK Schedule</Text>
                : 'PIK Schedule',
            value: facility?.pikScheduler ? 'True' : 'False',
        },
        {
            label: facility?.amortizationScheduleId
                ? <Text className='sectionItemTitleClickable' onClick={() =>
                    navigate(
                        `/dealmanagement/facilities/${facility?.id}/${facility?.status}/amortization-schedule`
                    )
                }>Amortization Schedule</Text>
                : 'Amortization Schedule',
            value: facility?.amortizationScheduleId ? "True" : "False",
        },
    ];

    const facilityContactsObjectArray = [
        {
            label: 'Facility Assignee',
            value: undefined,
        },
        {
            label: 'Administrative Agent Contact',
            value: contacts?.find(contact => contact.id === cleanUUID(facility?.opsAdmin?.id))?.name ?? '',
        }
    ]

    return {
        facilityDetailsObjectArray,
        facilityIdentifiersObjectArray,
        facilityFinancialsObjectArray,
        facilityContactsObjectArray,
    };
}
